import { db } from '../db/indexedDB';
import { Family, Member, Relationship, LibraryMember } from '../types';
import { neonService } from './neonService';

const DEMO_DISMISSED_KEY = 'tiafa:demo-dismissed';
const DEMO_FAMILY_NAME = 'Tiafa Family Tree';

function isDemoDismissed(): boolean {
  try {
    return localStorage.getItem(DEMO_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function setDemoDismissed(dismissed: boolean) {
  try {
    if (dismissed) localStorage.setItem(DEMO_DISMISSED_KEY, '1');
    else localStorage.removeItem(DEMO_DISMISSED_KEY);
  } catch {
    /* ignore */
  }
}

function isDemoFamily(family: Family | undefined | null): boolean {
  if (!family) return false;
  return family.userId === 'default' || family.name === DEMO_FAMILY_NAME;
}

// Background sync helper to avoid circular imports
function queueCloudSync(familyId: string) {
  neonService.schedulePush(familyId);
}

function generateId(): string {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
}

/**
 * Generates a beautiful SVG Data URI (Base64) with initials and gender-based colors
 */
export function getInitialsSvg(name: string, gender: 'M' | 'F'): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  let initials = '?';
  if (parts.length === 1) {
    initials = parts[0].slice(0, 2).toUpperCase();
  } else if (parts.length > 1) {
    initials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Soft/Premium colors for Dark & Light modes
  const bg = gender === 'M' ? '#1E88E5' : '#D81B60'; // Premium Blue / Premium Pink
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100%" height="100%" fill="${bg}"/>
      <text x="50%" y="54%" font-size="38" font-family="'Segoe UI', Arial, sans-serif" font-weight="bold" fill="#ffffff" dominant-baseline="middle" text-anchor="middle">
        ${initials}
      </text>
    </svg>
  `.trim();

  // ⭐ Encode as Base64 so library & browser treat it as a valid image source
  const base64Svg = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${base64Svg}`;
}

function normName(s: string | undefined): string {
  return (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export const familyService = {
  // === Family ===
    async createFamily(name: string, userId: string = 'default'): Promise<Family> {
    const family: Family = {
      id: generateId(),
      userId,
      name,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    await db.add('families', family);
    // Demo / anonymous local-only — Neon bill bachao
    if (userId !== 'default') {
      queueCloudSync(family.id);
    }
    return family;
  },

  async getFamilies(): Promise<Family[]> {
    return db.getAll<Family>('families');
  },

  async getFamily(id: string): Promise<Family | undefined> {
    return db.getById<Family>('families', id);
  },

    async updateFamily(family: Family): Promise<Family> {
    family.updatedAt = Date.now();
    const updated = await db.update('families', family);
    queueCloudSync(family.id);
    return updated;
  },

      async deleteFamily(id: string): Promise<void> {
    const family = await this.getFamily(id);

    const members = await this.getMembersByFamily(id);
    for (const m of members) {
      const rels = await this.getRelationshipsByMember(m.id);
      for (const r of rels) await db.delete('relationships', r.id);
      await db.delete('members', m.id);
    }
    await db.delete('families', id);

    // Demo delete = dismiss (refresh pe wapas na aaye)
    if (isDemoFamily(family)) {
      setDemoDismissed(true);
      return; // cloud touch mat karo
    }

    // Real owned family → cloud delete
    void neonService.deleteFamily(id);
  },

    // === Members ===
  async createMember(
    familyId: string,
    name: string,
    gender: 'M' | 'F',
    birthYear?: string,
    deathYear?: string,
    bio?: string,
    avatar?: string,
    qualification?: string,
    profession?: string,
    isDeceased?: boolean
  ): Promise<Member> {
    const member: Member = {
      id: generateId(),
      familyId,
      name,
      gender,
      birthYear,
      deathYear: isDeceased ? deathYear : undefined,
      bio,
      avatar,
      qualification,
      profession,
      isDeceased: !!isDeceased,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    await db.add('members', member);
    queueCloudSync(member.familyId);
    return member;
  },

  async getMembersByFamily(familyId: string): Promise<Member[]> {
    return db.getByIndex<Member>('members', 'familyId', familyId);
  },

  async getMember(id: string): Promise<Member | undefined> {
    return db.getById<Member>('members', id);
  },

  async updateMember(member: Member): Promise<Member> {
    member.updatedAt = Date.now();
    queueCloudSync(member.familyId);
    return db.update('members', member);
  },

  async deleteMember(id: string): Promise<void> {
  const member = await this.getMember(id);
  const rels = await this.getRelationshipsByMember(id);
  for (const r of rels) {
    await db.delete('relationships', r.id);
  }
  await db.delete('members', id);
  if (member?.familyId) {
    queueCloudSync(member.familyId);
  }
},

  // === Relationship Metadata Helper ===
  async updateRelationshipMetadata(
    relId: string, 
    metadata: Record<string, any>
  ): Promise<void> {
    const rel = await db.getById<Relationship>('relationships', relId);
    if (rel) {
      rel.metadata = { ...rel.metadata, ...metadata };
      await db.update('relationships', rel);
      queueCloudSync(rel.familyId);
    }
  },

    // === Relationships ===
  async createRelationship(
    familyId: string,
    sourceId: string,
    targetId: string,
    type: 'spouse' | 'parent' | 'child' | 'sibling',
    metadata?: Record<string, any>
  ): Promise<Relationship> {
    let nSource = sourceId;
    let nTarget = targetId;
    let nType: 'spouse' | 'parent' = 'spouse';

    if (type === 'parent') {
      nSource = sourceId;
      nTarget = targetId;
      nType = 'parent';
    } else if (type === 'child') {
      nSource = targetId;
      nTarget = sourceId;
      nType = 'parent';
    } else {
      nType = 'spouse';
    }

    const existing = await this.getRelationshipByPair(nSource, nTarget);
    if (existing) return existing;

    const rel: Relationship = {
      id: generateId(),
      familyId,
      sourceId: nSource,
      targetId: nTarget,
      type: nType,
      metadata
    };
    await db.add('relationships', rel);

    // Smart-link after manual create
    await this.applySmartLinking(familyId);
    queueCloudSync(familyId);
    return rel;
  },

  async getRelationshipsByFamily(familyId: string): Promise<Relationship[]> {
    return db.getByIndex<Relationship>('relationships', 'familyId', familyId);
  },

  async getRelationshipsByMember(memberId: string): Promise<Relationship[]> {
    const all = await db.getAll<Relationship>('relationships');
    return all.filter((r) => r.sourceId === memberId || r.targetId === memberId);
  },

  async getRelationshipByPair(
    sourceId: string,
    targetId: string
  ): Promise<Relationship | undefined> {
    const all = await db.getAll<Relationship>('relationships');
    return all.find(
      (r) =>
        (r.sourceId === sourceId && r.targetId === targetId) ||
        (r.sourceId === targetId && r.targetId === sourceId)
    );
  },

  async deleteRelationship(id: string): Promise<void> {
    const rel = await db.getById<Relationship>('relationships', id);
    await db.delete('relationships', id);
    if (rel?.familyId) {
      queueCloudSync(rel.familyId);
    }
  },

  /**
   * SMART LINKING
   * 1) Child has 1 parent + that parent has spouse → spouse becomes 2nd parent
   * 2) Child has 2 parents who aren't spouses → auto-marry them
   */
  async applySmartLinking(familyId: string): Promise<void> {
    const rels = await this.getRelationshipsByFamily(familyId);
    const members = await this.getMembersByFamily(familyId);
    const memberIds = new Set(members.map((m) => m.id));

    const parentsOf = new Map<string, string[]>();
    const spousesOf = new Map<string, string[]>();

    for (const r of rels) {
      if (!memberIds.has(r.sourceId) || !memberIds.has(r.targetId)) continue;
      if (r.type === 'parent') {
        const arr = parentsOf.get(r.targetId) || [];
        if (!arr.includes(r.sourceId)) arr.push(r.sourceId);
        parentsOf.set(r.targetId, arr);
      } else if (r.type === 'spouse') {
        const a = spousesOf.get(r.sourceId) || [];
        const b = spousesOf.get(r.targetId) || [];
        if (!a.includes(r.targetId)) a.push(r.targetId);
        if (!b.includes(r.sourceId)) b.push(r.sourceId);
        spousesOf.set(r.sourceId, a);
        spousesOf.set(r.targetId, b);
      }
    }

    const relKeys = new Set(
      rels.map((r) => `${r.type}:${[r.sourceId, r.targetId].sort().join(':')}`)
    );

    const addRel = async (
      sourceId: string,
      targetId: string,
      type: 'parent' | 'spouse'
    ) => {
      const key =
        type === 'spouse'
          ? `spouse:${[sourceId, targetId].sort().join(':')}`
          : `parent:${[sourceId, targetId].sort().join(':')}`;
      const pairKey =
        type === 'spouse'
          ? `spouse:${[sourceId, targetId].sort().join(':')}`
          : `parent:${sourceId}:${targetId}`;
      if (relKeys.has(pairKey) || relKeys.has(key)) return;
      if (type === 'parent' && relKeys.has(`parent:${targetId}:${sourceId}`)) return;

      await db.add('relationships', {
        id: generateId(),
        familyId,
        sourceId,
        targetId,
        type
      });
      relKeys.add(pairKey);
      console.log(`🔗 Smart-Link: ${type} ${sourceId.slice(0, 8)} → ${targetId.slice(0, 8)}`);
    };

    // Rule 1: single parent → add spouse as 2nd parent
    for (const [childId, parents] of parentsOf.entries()) {
      if (parents.length !== 1) continue;
      const p = parents[0];
      const spouses = spousesOf.get(p) || [];
      for (const sp of spouses) {
        await addRel(sp, childId, 'parent');
        const childParents = parentsOf.get(childId) || [];
        if (!childParents.includes(sp)) childParents.push(sp);
        parentsOf.set(childId, childParents);
      }
    }

    // Rule 2: two parents → ensure they are spouses
    for (const [, parents] of parentsOf.entries()) {
      if (parents.length < 2) continue;
      for (let i = 0; i < parents.length; i++) {
        for (let j = i + 1; j < parents.length; j++) {
          await addRel(parents[i], parents[j], 'spouse');
        }
      }
    }
  },

  /**
   * Merge duplicate members (same name + gender + birthYear) inside a family.
   * Keeps the oldest record as canonical, rewires relationships, deletes dupes.
   */
  async deduplicateMembers(familyId: string): Promise<number> {
    const members = await this.getMembersByFamily(familyId);
    const groups = new Map<string, Member[]>();

    for (const m of members) {
      const key = `${normName(m.name)}|${m.gender}|${(m.birthYear || '').trim()}`;
      const arr = groups.get(key) || [];
      arr.push(m);
      groups.set(key, arr);
    }

    let removed = 0;
    const idMap = new Map<string, string>();

    for (const [, group] of groups) {
      if (group.length < 2) continue;
      group.sort((a, b) => a.createdAt - b.createdAt);
      const canonical = group[0];
      for (let i = 1; i < group.length; i++) {
        idMap.set(group[i].id, canonical.id);
      }
    }

    if (idMap.size === 0) return 0;

    const rels = await this.getRelationshipsByFamily(familyId);

    // Rewire relationships
    for (const rel of rels) {
      const newSource = idMap.get(rel.sourceId) || rel.sourceId;
      const newTarget = idMap.get(rel.targetId) || rel.targetId;

      if (newSource === rel.sourceId && newTarget === rel.targetId) continue;

      if (newSource === newTarget) {
        await db.delete('relationships', rel.id);
        continue;
      }

      const conflict = rels.find(
        (r) =>
          r.id !== rel.id &&
          r.type === rel.type &&
          ((r.sourceId === newSource && r.targetId === newTarget) ||
            (rel.type === 'spouse' &&
              r.sourceId === newTarget &&
              r.targetId === newSource) ||
            (rel.type === 'parent' &&
              r.sourceId === newSource &&
              r.targetId === newTarget))
      );

      if (conflict) {
        await db.delete('relationships', rel.id);
      } else {
        await db.update('relationships', {
          ...rel,
          sourceId: newSource,
          targetId: newTarget
        });
      }
    }

    // Delete duplicate members
    for (const dupeId of idMap.keys()) {
      await db.delete('members', dupeId);
      removed++;
      console.log(`🧹 Merged duplicate member ${dupeId.slice(0, 8)} → ${idMap.get(dupeId)!.slice(0, 8)}`);
    }

    return removed;
  },

  /** Full cleanup: dedupe + smart link */
  async repairFamily(familyId: string): Promise<void> {
    const removed = await this.deduplicateMembers(familyId);
    await this.applySmartLinking(familyId);
    if (removed > 0) {
      console.log(`🛠️ Repair: removed ${removed} duplicate(s), smart-link applied`);
    }
  },
  
  // === Library format ===
  toLibraryFormat(members: Member[], relationships: Relationship[]): LibraryMember[] {
    const memberMap: { [key: string]: LibraryMember } = {};

    members.forEach((m) => {
      memberMap[m.id] = {
        id: m.id,
        data: {
          'first name': m.name.split(' ')[0] || m.name,
          'last name': m.name.split(' ').slice(1).join(' ') || '',
          gender: m.gender,
          birthday: m.birthYear || '',
          avatar: m.avatar || '', // ⭐ Only pass real photo URL so library renders image nicely
          bio: m.bio || ''
        },
        rels: { parents: [], spouses: [], children: [] }
      };
    });

    relationships.forEach((r) => {
      const source = memberMap[r.sourceId];
      const target = memberMap[r.targetId];
      if (!source || !target) return;

      if (r.type === 'parent') {
        target.rels.parents.push(r.sourceId);
        source.rels.children.push(r.targetId);
      } else if (r.type === 'spouse') {
        source.rels.spouses.push(r.targetId);
        target.rels.spouses.push(r.sourceId);
      }
    });

    Object.values(memberMap).forEach((m) => {
      m.rels.parents = [...new Set(m.rels.parents)];
      m.rels.spouses = [...new Set(m.rels.spouses)];
      m.rels.children = [...new Set(m.rels.children)];
    });

    return Object.values(memberMap);
  },

    // === Sync FROM library UI → IndexedDB ===
  async syncFromLibraryFormat(
    familyId: string,
    rawLibraryData: LibraryMember[]
  ): Promise<void> {
    console.log('🔄 Reconciling UI with IndexedDB...');

    // 1) Drop placeholders / ADD cards / incomplete nodes
    const libraryData = rawLibraryData.filter((m) => {
      if (!m || !m.id) return false;
      if ((m as any).to_add || (m as any)._new_rel_data || (m as any).unknown) return false;
      if ((m as any).to_add === true) return false;
      const fn = String(m.data?.['first name'] || '').trim();
      const ln = String(m.data?.['last name'] || '').trim();
      const full = `${fn} ${ln}`.trim();
      if (!fn || fn.toUpperCase() === 'ADD' || full.toUpperCase() === 'ADD') return false;
      if (!m.data?.gender || (m.data.gender !== 'M' && m.data.gender !== 'F')) return false;
      return true;
    });

    const existingMembers = await this.getMembersByFamily(familyId);
    const existingById = new Map(existingMembers.map((m) => [m.id, m]));
    const incomingIds = new Set(libraryData.map((m) => m.id));

    // 2) Delete members removed in UI
    for (const existing of existingMembers) {
      if (!incomingIds.has(existing.id)) {
        await this.deleteMember(existing.id);
      }
    }

    // 3) Upsert members
    for (const libMem of libraryData) {
      const fullName = [libMem.data['first name'], libMem.data['last name']]
        .map((s) => (s || '').trim())
        .filter(Boolean)
        .join(' ');
      const gender = libMem.data.gender as 'M' | 'F';
      const birthYear = libMem.data.birthday || undefined;
      const bio = libMem.data.bio || undefined;
      const avatar = libMem.data.avatar || undefined;

      if (existingById.has(libMem.id)) {
        const prev = existingById.get(libMem.id)!;
        await db.update('members', {
          ...prev,
          name: fullName || prev.name,
          gender: gender || prev.gender,
          birthYear,
          bio,
          avatar,
          updatedAt: Date.now()
        });
      } else {
        await db.add('members', {
          id: libMem.id,
          familyId,
          name: fullName || 'Unknown',
          gender: gender || 'M',
          birthYear,
          bio,
          avatar,
          createdAt: Date.now(),
          updatedAt: Date.now()
        });
      }
    }

    // 4) Rebuild relationships cleanly
    const oldRels = await this.getRelationshipsByFamily(familyId);
    for (const r of oldRels) await db.delete('relationships', r.id);

    const added = new Set<string>();
    const validIds = new Set(libraryData.map((m) => m.id));

    const putRel = async (
      sourceId: string,
      targetId: string,
      type: 'parent' | 'spouse'
    ) => {
      if (!validIds.has(sourceId) || !validIds.has(targetId)) return;
      if (sourceId === targetId) return;
      const key =
        type === 'spouse'
          ? `spouse:${[sourceId, targetId].sort().join(':')}`
          : `parent:${sourceId}:${targetId}`;
      if (added.has(key)) return;
      added.add(key);
      await db.add('relationships', {
        id: generateId(),
        familyId,
        sourceId,
        targetId,
        type
      });
    };

    for (const libMem of libraryData) {
      const myId = libMem.id;
      for (const pId of libMem.rels?.parents || []) {
        await putRel(pId, myId, 'parent');
      }
      for (const sId of libMem.rels?.spouses || []) {
        const [a, b] = [myId, sId].sort();
        await putRel(a, b, 'spouse');
      }
      for (const cId of libMem.rels?.children || []) {
        await putRel(myId, cId, 'parent');
      }
    }

    // 5) Dedupe + smart link (fixes duplicate Bharti + Twisha single-parent)
    await this.deduplicateMembers(familyId);
    await this.applySmartLinking(familyId);

    console.log('✅ DB Synced successfully!');
    
    // ☁️ Trigger background sync to Neon cloud
    queueCloudSync(familyId);
  },

    async seedDemoData(): Promise<void> {
    const families = await this.getFamilies();
    if (families.length > 0) return;
    if (isDemoDismissed()) return; // user ne pehle delete kiya tha

    // Local-only demo (userId default → cloud push nahi hoga)
    const f = await this.createFamily(DEMO_FAMILY_NAME, 'default');
    const dada = await this.createMember(f.id, 'Grandfather Tiafa', 'M', '1950');
    const dadi = await this.createMember(f.id, 'Grandmother Tiafa', 'F', '1953');
    const papa = await this.createMember(f.id, 'Father Tiafa', 'M', '1975');
    const mummy = await this.createMember(f.id, 'Mother Tiafa', 'F', '1978');
    const tejansh = await this.createMember(f.id, 'Tejansh', 'M', '2005');

    await this.createRelationship(f.id, dada.id, dadi.id, 'spouse');
    await this.createRelationship(f.id, dada.id, papa.id, 'parent');
    await this.createRelationship(f.id, dadi.id, papa.id, 'parent');
    await this.createRelationship(f.id, papa.id, mummy.id, 'spouse');
    await this.createRelationship(f.id, papa.id, tejansh.id, 'parent');
    await this.createRelationship(f.id, mummy.id, tejansh.id, 'parent');
  },

  /** User ne demo delete kiya ho → 1-click wapas */
  async restoreDemoTree(): Promise<Family | null> {
    setDemoDismissed(false);

    // Agar pehle se koi Tiafa demo pada hai to usko hi use karo
    const existing = (await this.getFamilies()).find(
      (f) => f.userId === 'default' || f.name === DEMO_FAMILY_NAME
    );
    if (existing) return existing;

    // Fresh local seed (cloud nahi)
    const before = await this.getFamilies();
    // force path: temporarily ensure empty check bypass by seeding only if no demo
    const f = await this.createFamily(DEMO_FAMILY_NAME, 'default');
    const dada = await this.createMember(f.id, 'Grandfather Tiafa', 'M', '1950');
    const dadi = await this.createMember(f.id, 'Grandmother Tiafa', 'F', '1953');
    const papa = await this.createMember(f.id, 'Father Tiafa', 'M', '1975');
    const mummy = await this.createMember(f.id, 'Mother Tiafa', 'F', '1978');
    const tejansh = await this.createMember(f.id, 'Tejansh', 'M', '2005');

    await this.createRelationship(f.id, dada.id, dadi.id, 'spouse');
    await this.createRelationship(f.id, dada.id, papa.id, 'parent');
    await this.createRelationship(f.id, dadi.id, papa.id, 'parent');
    await this.createRelationship(f.id, papa.id, mummy.id, 'spouse');
    await this.createRelationship(f.id, papa.id, tejansh.id, 'parent');
    await this.createRelationship(f.id, mummy.id, tejansh.id, 'parent');

    void before; // silence lint if any
    return f;
  },

  async clearAllData(): Promise<void> {
    await db.clear('families');
    await db.clear('members');
    await db.clear('relationships');
  }
};